// Passkeys settings section (docs/03 §2). A passkey signs THIS device into the
// server AND — with the modern WebAuthn PRF extension — can now restore the E2E
// identity on a NEW device: the passkey derives a secret the server never sees,
// which wraps the identity seed; the server stores only the ciphertext (same
// threat model as the password restore, docs/03 §3a). PRF is feature-detected
// per authenticator: when it's unavailable the passkey still works for session
// login but is session-only, and the UI says so — the recovery phrase/password
// remain the restore path. List/delete run straight against the REST client
// from useScreens(); prime/register run through the shared brain
// (useAccount().credentials), which owns the ceremony sequencing and the
// options-prefetch cache — this component just drives it. The WebAuthn
// ceremony itself is feature-detected too (Capacitor WebViews may lack it).

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { PasskeySummary } from '@merkury/protocol';
import { isRelatedOriginError, supportsWebAuthn } from '../../identity/index.js';
import { useT, type TFunc } from '../../i18n/index.js';
import { RestError } from '@merkury/brain/api/client.js';
import { useScreens } from '../ScreensContext.js';
import { useAccount } from '../../shell/accountContext.js';

/**
 * Map a passkey-add failure to user copy. The failure is one of three shapes:
 * a `DOMException` from `navigator.credentials.create` (client-side), a
 * `RestError` from the register POST (server-side), or a ROR rejection on a
 * mirror origin. We name the two common real causes so the message is
 * actionable instead of a blanket "try again":
 *  - `InvalidStateError` (create) / 409 (server UNIQUE): a passkey for this RP
 *    already lives on this authenticator — the user must remove it first.
 *  - a 400 "no passkey registration in progress": the prefetched challenge
 *    aged out past its 5-minute TTL (or a second tab replaced it) — retry.
 */
export function passkeyAddMessage(
  err: unknown,
  rpId: string | undefined,
  t: TFunc,
  origin?: string,
): string {
  // On a mirror domain a browser without Related Origin Requests support
  // (Firefox) rejects the pinned RP ID; point the user at the primary site.
  // `origin` defaults (inside isRelatedOriginError) to the current page origin.
  if (isRelatedOriginError(err, rpId, origin))
    return t('passkeys.wrongOrigin', { domain: rpId ?? '' });
  const name = (err as { name?: unknown } | null | undefined)?.name;
  if (err instanceof RestError) {
    if (err.status === 409) return t('passkeys.alreadyOnDevice');
    if (err.status === 400) return t('passkeys.expiredRetry');
    return t('passkeys.couldNotAdd');
  }
  if (name === 'InvalidStateError') return t('passkeys.alreadyOnDevice');
  return t('passkeys.couldNotAdd');
}

const buttonClass =
  'min-h-touch rounded-xl bg-accent px-4 text-sm font-medium text-ink disabled:opacity-40 active:bg-accent-press';
const inputClass = 'min-h-touch rounded-xl border border-edge bg-raised px-3 text-sm text-ink';

// No `identity` prop: the seed never reaches the UI layer any more. The brain
// holds it and `credentials.addPasskey` does the PRF wrap internally, which is
// the point of the merge — one place that touches the identity seed.
export function PasskeysSection(): ReactNode {
  const t = useT();
  const { api } = useScreens();
  const { credentials } = useAccount();
  const [passkeys, setPasskeys] = useState<PasskeySummary[] | undefined>(undefined);
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const supported = supportsWebAuthn();

  const refresh = useCallback(async () => {
    try {
      const { passkeys: list } = await api.listPasskeys();
      setPasskeys(list);
    } catch {
      setPasskeys([]);
      setError(t('passkeys.couldNotLoad'));
    }
  }, [api, t]);

  // Registration options prefetched before the click: the server round-trip
  // must NOT sit between the button gesture and navigator.credentials.create,
  // or Safari drops the transient activation (web/passkey-audit.md §1). Warmed
  // on mount and re-warmed after each use (the challenge is single-use); the
  // brain holds the prefetched options and `addPasskey` falls back to an
  // inline fetch if this hasn't landed yet. Best-effort: swallow failures here
  // too (not just inside the brain's own REST try/catch) — this always runs
  // fire-and-forget (`void primeOptions()`), so an uncaught rejection here
  // would surface as an unhandled rejection instead of a harmless no-op.
  const primeOptions = useCallback(async (): Promise<void> => {
    if (!supported) return;
    await credentials.primeAddPasskey(api).catch(() => undefined);
  }, [api, supported, credentials]);

  useEffect(() => {
    void refresh();
    void primeOptions();
  }, [refresh, primeOptions]);

  async function register(): Promise<void> {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      // PRF wrap ceremony (brain/runtime/account.ts): the passkey derives a
      // server-invisible secret over the FIXED app-wide restore salt and
      // encrypts the identity seed under it — so the one-tap discoverable
      // restore can unwrap it with no account id. If the authenticator lacks
      // PRF, the passkey still registers session-only (restorable=false) —
      // never failing the whole registration (docs/03 §2).
      const added = await credentials.addPasskey(api, label);
      if (added === null) return; // no credential (e.g. cancelled)
      setNotice(added.restorable ? t('passkeys.restorable') : t('passkeys.sessionOnly'));
      setLabel('');
      await refresh();
      void primeOptions(); // warm a fresh challenge for the next add
    } catch (err) {
      // Surface the real cause: this failure was previously an undiagnosable
      // generic message. The console line names the DOMException / RestError so
      // a field report is actionable; it carries no tokens/IPs/UUIDs.
      console.error('[passkey] add failed', err);
      // The web passkey provider (identity/webPasskeys.ts) stamps the
      // server-pinned RP ID onto the thrown error — this screen no longer
      // holds the registration options itself to read it off directly.
      const rpId = (err as { rpId?: string }).rpId;
      setError(passkeyAddMessage(err, rpId, t));
      void primeOptions(); // the consumed challenge is spent — re-arm for a retry
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    setError(undefined);
    try {
      await api.deletePasskey(id);
      await refresh();
    } catch {
      setError(t('passkeys.couldNotRemove'));
    }
  }

  return (
    <section aria-label={t('passkeys.title')} className="flex flex-col gap-3 rounded-xl bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">{t('passkeys.title')}</h2>
      <p className="text-xs text-muted">{t('passkeys.help')}</p>

      {!supported && (
        <p role="note" className="text-xs text-muted">
          {t('passkeys.unsupported')}
        </p>
      )}

      <ul className="flex flex-col gap-2" aria-label={t('passkeys.registered')}>
        {passkeys === undefined ? (
          <li className="text-xs text-muted" role="status">
            {t('common.loading')}
          </li>
        ) : passkeys.length === 0 ? (
          <li className="text-xs text-muted">{t('passkeys.none')}</li>
        ) : (
          passkeys.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2">
              <span className="truncate text-sm text-ink">{p.label || t('passkeys.defaultLabel')}</span>
              <button
                type="button"
                onClick={() => void remove(p.id)}
                className="min-h-touch rounded-xl px-3 text-sm font-medium text-danger active:opacity-70"
              >
                {t('passkeys.remove')}
              </button>
            </li>
          ))
        )}
      </ul>

      {error !== undefined && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      {notice !== undefined && (
        <p role="status" className="text-xs text-muted">
          {notice}
        </p>
      )}

      {supported && (
        <div className="flex gap-2">
          <input
            className={`${inputClass} flex-1`}
            placeholder={t('passkeys.namePlaceholder')}
            value={label}
            maxLength={64}
            onChange={(e) => setLabel(e.target.value)}
            aria-label={t('passkeys.newNameAria')}
          />
          <button type="button" className={buttonClass} disabled={busy} onClick={() => void register()}>
            {busy ? t('passkeys.adding') : t('passkeys.add')}
          </button>
        </div>
      )}
    </section>
  );
}
